import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Flex, Text } from "@radix-ui/themes";
import { signIn } from "next-auth/react";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { Dispatch, SetStateAction, useState } from "react";
import ForgotPassword from "./ForgotPassword";
import { Loader2 } from "lucide-react"
import PasswordInput from "@/app/components/form/PasswordInput";
import { cn } from "@/lib/utils";

interface CredentialsFormsProps {
  loggingIn: boolean;
  setLoggingIn: Dispatch<SetStateAction<boolean>>;
  callbackURL: string;
  router: AppRouterInstance;
  onClickForgot?: any;
  onComplete?: any;
  forgot: boolean;
  setForgot: Dispatch<SetStateAction<boolean>>;
  onClickSignUp?: () => void;
}

export function CredentialsSignInForm({
  loggingIn,
  setLoggingIn,
  callbackURL,
  router,
  onClickForgot,
  onComplete,
  forgot,
  setForgot,
  onClickSignUp
}: CredentialsFormsProps) {
  const [error, setError] = useState("");

  async function handleSubmit(e: any) {
    e.preventDefault();
    setLoggingIn(true);
    const formData = new FormData(e.target);

    signIn("credentials", {
      email: formData.get("email"),
      password: formData.get("password"),
      callbackUrl: callbackURL,
      redirect: false,
    }).then((response) => {
      if (response?.ok) {
        router.push(callbackURL);
        router.refresh();
        if (onComplete) onComplete();
      } else {
        setError("Incorrect email or password.");
        setLoggingIn(false);
      }
    });
  }

  if (forgot)
    return (
      <ForgotPassword
        setForgot={setForgot}
        loggingIn={loggingIn}
        setLoggingIn={setLoggingIn}
        router={router}
        onComplete={onComplete}
      />
    );

  if (!forgot)
    return (
      <form className="w-full" onSubmit={handleSubmit}>
        <Flex direction="column">
          <Flex direction="column" className={cn("w-full gap-2 mb-2", error && "mb-2")}>
            <Input
              disabled={loggingIn}
              placeholder="Email"
              className="w-full h-12 rounded-md"
              type="email"
              name="email"
              required={true}
              onChange={() => {
                error ? setError("") : null;
              }}
            />
            <PasswordInput
              disabled={loggingIn}
              placeholder="Password"
              className="w-full h-12 rounded-md"
              type="password"
              name="password"
              required={true}
              onChange={() => {
                error ? setError("") : null;
              }}
            />
            {error && <Flex justify="center">
              <Text className="text-red-600">{error}</Text>
            </Flex>}
          </Flex>
          <Flex direction="column" position="relative" className="mt-1">
            <Button
              disabled={loggingIn}
              type="submit"
              className={"color-inverting-button-base h-12 hover:bg-white w-full"}
            >
              {loggingIn && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </Flex>

          <Flex justify="between" align="center" className="w-full pt-2 mt-1">
            <Text
              onClick={() => {
                if (!loggingIn) {
                  if (onClickForgot) onClickForgot();
                  else setForgot(true);
                }
              }}
              className="text-sm font-medium hover:underline hover:underline-offset-2 text-slate-600 cursor-pointer"
            >
              Forgot password?
            </Text>
            {!forgot && onClickSignUp && (
              <Text
                onClick={() => {
                  if (!loggingIn) onClickSignUp();
                }}
                className="text-sm font-medium text-slate-600 cursor-pointer text-right"
              >
                <span className="hidden md:inline">
                  <span className="text-slate-600">New user?</span> <span className="hover:underline hover:underline-offset-2 text-[#625bf6] font-medium">Create an account</span>
                </span>
                <span className="md:hidden hover:underline hover:underline-offset-2 text-[#625bf6] font-medium">Sign up</span>
              </Text>
            )}
          </Flex>
        </Flex>
      </form >
    );
}
